import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Globe, Loader2, Trash2, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  if (status === "verified") {
    return <Badge className="rounded-none bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 gap-1.5 uppercase tracking-widest text-[10px]"><CheckCircle2 className="w-3 h-3" /> Verified</Badge>;
  }
  if (status === "pending") {
    return <Badge className="rounded-none bg-amber-500/15 text-amber-300 border border-amber-500/30 gap-1.5 uppercase tracking-widest text-[10px]"><Clock className="w-3 h-3" /> Pending</Badge>;
  }
  return <Badge className="rounded-none bg-white/5 text-white/50 border border-white/15 uppercase tracking-widest text-[10px]">None</Badge>;
}

export default function AdminDomains() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releasingId, setReleasingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/super-admin/domains");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const release = async (row) => {
    setReleasingId(row.tenant_id);
    try {
      await api.delete(`/super-admin/tenants/${row.tenant_id}/domain`);
      toast.success(`Released ${row.custom_domain} from ${row.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setReleasingId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 border border-white/15 flex items-center justify-center text-amber-300">
            <Globe className="w-4 h-4" />
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-domains-title">Custom domains</h1>
        </div>
        <p className="text-white/55 text-sm max-w-xl">
          Every studio domain across the platform. Release a domain to free it up so another studio can connect it.
        </p>
      </div>

      <div className="bg-[#121212] border border-white/10" data-testid="admin-domains-card">
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-white/40 text-sm uppercase tracking-widest">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Globe className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm">No custom domains connected yet.</p>
            <p className="text-white/30 text-xs mt-1">Studios connect domains from Studio → Domains, or during onboarding.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/8">
            {/* header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-white/35">
              <div className="col-span-4">Domain</div>
              <div className="col-span-4">Studio</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {rows.map((row) => (
              <div key={row.tenant_id} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 py-4 items-center" data-testid={`domain-row-${row.slug}`}>
                <div className="md:col-span-4 flex items-center gap-2 min-w-0">
                  <Globe className="w-4 h-4 text-white/40 shrink-0" />
                  <code className="text-sm text-white/90 truncate">{row.custom_domain}</code>
                  {row.custom_domain_status === "verified" && (
                    <a href={`https://${row.custom_domain}`} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white shrink-0" title="Visit">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="md:col-span-4 min-w-0">
                  <div className="text-sm text-white truncate">{row.name}</div>
                  <div className="text-[11px] text-white/40 font-mono truncate">/s/{row.slug}</div>
                </div>
                <div className="md:col-span-2"><StatusBadge status={row.custom_domain_status} /></div>
                <div className="md:col-span-2 md:text-right">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={releasingId === row.tenant_id}
                        className="rounded-none text-white/60 hover:text-red-400 uppercase text-[10px] tracking-[0.2em]"
                        data-testid={`domain-release-${row.slug}`}
                      >
                        {releasingId === row.tenant_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Release</>}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#0a0a0a] border border-white/15 rounded-none text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">Release {row.custom_domain}?</AlertDialogTitle>
                        <AlertDialogDescription className="text-white/60">
                          This disconnects <span className="text-white">{row.custom_domain}</span> from <span className="text-white">{row.name}</span>.
                          The studio will fall back to its <code className="text-white/70">/s/{row.slug}</code> URL, and the domain becomes available for any studio to connect.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none bg-transparent border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => release(row)} className="rounded-none bg-red-500 text-white hover:bg-red-600" data-testid={`domain-release-confirm-${row.slug}`}>
                          Release domain
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
